import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GoogleCalendarConnect } from "@/components/controller/calendar/GoogleCalendarConnect";
import { useWODocumentSettings, useUpsertWODocumentSettings, DEFAULT_TOGGLES, TOGGLE_KEYS } from "@/hooks/useWODocumentSettings";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RequirePermission } from "@/components/RequirePermission";
import { usePermissions } from "@/hooks/usePermissions";
import { AuditLogTable } from "@/components/controller/AuditLogTable";
import { ProductsServicesTab } from "@/components/controller/settings/ProductsServicesTab";
import { ChartOfAccountsTab } from "@/components/controller/settings/ChartOfAccountsTab";
import { UsersTab } from "@/components/controller/settings/UsersTab";
import { RolesTab } from "@/components/controller/settings/RolesTab";
import { AppearanceTab } from "@/components/controller/settings/AppearanceTab";
import { ApprovalRulesTab } from "@/components/controller/settings/ApprovalRulesTab";
import { AlertThresholdsTab } from "@/components/controller/settings/AlertThresholdsTab";
import { NumberingTab } from "@/components/controller/settings/NumberingTab";
import { CompanyProfileTab } from "@/components/controller/settings/CompanyProfileTab";
import { useQBStatus } from "@/hooks/useQBStatus";
import { useQBImport } from "@/hooks/useQBImport";
import { QBImportWizard } from "@/components/controller/settings/QBImportWizard";
import { SyncLogDetailModal } from "@/components/controller/settings/SyncLogDetailModal";
import { useOpsAuditLog } from "@/hooks/useOpsAuditLog";
import { useLedgerAuditLog } from "@/hooks/useLedgerAuditLog";
import { useEstimateTemplates, useCreateTemplate, useUpdateTemplate, useDeleteTemplate, EstimateTemplate, TemplateLineItem } from "@/hooks/useEstimateTemplates";
import { TemplateLineEditor } from "@/components/controller/settings/TemplateLineEditor";
import { EstimateProposalPresetsSection } from "@/components/controller/settings/EstimateProposalPresetsSection";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, Plus, Pencil, Trash2, Copy, Download, RefreshCw, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { useQuery } from "@tanstack/react-query";
import { useTimesheetApprovalSetting } from "@/hooks/useTimesheetApprovalSetting";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// TODO: Decommission sandbox project (maltrtjtputdznaloovz) once consolidated migration is validated in production.
const QB_FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qb-sync`;

const ENTITY_FILTERS = ["all", "account", "customer", "estimate", "invoice", "expense"] as const;
const ENTITY_LABELS: Record<string, string> = {
  all: "All", account: "Accounts", customer: "Customers", estimate: "Estimates", invoice: "Invoices", expense: "Expenses",
};

interface SyncRun {
  time: string;
  timeLabel: string;
  entries: any[];
  totalImported: number;
  hasErrors: boolean;
}

function SyncLogGrouped({ logs }: { logs: any[] }) {
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [detailRow, setDetailRow] = useState<{ entityType: string; syncTime: string; count: number } | null>(null);

  // Filter by entity type
  const filtered = entityFilter === "all" ? logs : logs.filter((r) => r.entity_type === entityFilter);

  // Group by sync run (entries within 30 seconds of each other)
  const runs: SyncRun[] = [];
  let currentRun: SyncRun | null = null;

  for (const row of filtered) {
    const rowTime = new Date(row.created_at).getTime();
    if (!currentRun || rowTime < currentRun.entries[currentRun.entries.length - 1]._time - 30_000) {
      currentRun = {
        time: row.created_at,
        timeLabel: format(new Date(row.created_at), "MMM d, h:mm a"),
        entries: [],
        totalImported: 0,
        hasErrors: false,
      };
      runs.push(currentRun);
    }
    const payload = typeof row.response_payload === "string" ? JSON.parse(row.response_payload) : row.response_payload;
    row._payload = payload;
    row._time = rowTime;
    currentRun.entries.push(row);
    if (payload?.imported) currentRun.totalImported += payload.imported;
    if (row.status === "error") currentRun.hasErrors = true;
  }

  const toggleRun = (key: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {/* Entity filter tabs */}
      <div className="flex gap-1 flex-wrap">
        {ENTITY_FILTERS.map((f) => (
          <Button
            key={f}
            variant={entityFilter === f ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => setEntityFilter(f)}
          >
            {ENTITY_LABELS[f]}
          </Button>
        ))}
      </div>

      {runs.length === 0 ? (
        <p className="text-center py-6 text-sm text-muted-foreground">No sync history found.</p>
      ) : (
        <div className="space-y-2">
          {runs.map((run, ri) => {
            const key = `${run.time}-${ri}`;
            const isExpanded = expandedRuns.has(key);
            const entityCount = run.entries.length;
            const entityTypes = run.entries.map((e: any) => e.entity_type).join(", ");

            return (
              <div key={key} className="border rounded-md overflow-hidden">
                {/* Run header — clickable to expand */}
                <button
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-accent/50 transition-colors"
                  onClick={() => toggleRun(key)}
                >
                  {run.hasErrors ? (
                    <XCircle className="h-4 w-4 text-destructive shrink-0" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{run.timeLabel}</span>
                      <Badge variant="secondary" className="text-[10px]">{entityCount} entit{entityCount !== 1 ? "ies" : "y"}</Badge>
                      {run.totalImported > 0 && (
                        <span className="text-xs text-muted-foreground">{run.totalImported} records imported</span>
                      )}
                    </div>
                    {!isExpanded && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{entityTypes}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{isExpanded ? "▲" : "▼"}</span>
                </button>

                {/* Expanded detail rows */}
                {isExpanded && (
                  <div className="border-t">
                    <Table>
                      <TableBody>
                        {run.entries.map((row: any) => {
                          const payload = row._payload;
                          const isError = row.status === "error";
                          return (
                            <TableRow
                              key={row.id}
                              className={`${isError ? "bg-destructive/5" : ""} ${!isError && payload?.imported > 0 ? "cursor-pointer hover:bg-accent/50" : ""}`}
                              onClick={() => {
                                if (!isError && payload?.imported > 0) {
                                  setDetailRow({ entityType: row.entity_type, syncTime: row.created_at, count: payload.imported });
                                }
                              }}
                            >
                              <TableCell className="py-1.5 w-8">
                                {isError ? <XCircle className="h-3.5 w-3.5 text-destructive" /> :
                                 row.status === "partial" ? <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" /> :
                                 <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />}
                              </TableCell>
                              <TableCell className="py-1.5">
                                <Badge variant="outline" className="text-[10px]">{row.sync_type}</Badge>
                              </TableCell>
                              <TableCell className="py-1.5 text-sm font-medium">{row.entity_type}</TableCell>
                              <TableCell className="py-1.5 text-sm text-muted-foreground">
                                {row.error_message ? (
                                  <span className="text-destructive">{row.error_message}</span>
                                ) : payload ? (
                                  <span>
                                    {payload.imported != null && `${payload.imported} imported`}
                                    {payload.bills_created > 0 && `, ${payload.bills_created} bills`}
                                    {payload.skipped_duplicates > 0 && `, ${payload.skipped_duplicates} skipped`}
                                    {payload.skipped_no_project > 0 && `, ${payload.skipped_no_project} skipped (no project)`}
                                    {payload.failed > 0 && `, ${payload.failed} failed`}
                                  </span>
                                ) : "—"}
                              </TableCell>
                              <TableCell className="py-1.5 text-right text-xs text-muted-foreground whitespace-nowrap">
                                {format(new Date(row.created_at), "h:mm:ss a")}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {detailRow && (
        <SyncLogDetailModal
          open={!!detailRow}
          onClose={() => setDetailRow(null)}
          entityType={detailRow.entityType}
          syncTime={detailRow.syncTime}
          importedCount={detailRow.count}
        />
      )}
    </div>
  );
}

const Settings = () => {
  const { connected, lastSyncedAt, isLoading, companyId, refetch } = useQBStatus();
  const { runImport, importing } = useQBImport();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnecting, setDisconnecting] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [opsFilter, setOpsFilter] = useState<string>("all");
  const { can, isAdmin } = usePermissions();

  // QB sync log
  const { data: qbSyncLog = [], isLoading: syncLogLoading, refetch: refetchSyncLog } = useQuery({
    queryKey: ["qb_sync_log", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("qb_sync_log" as any)
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!companyId,
  });

  const { data: opsRows = [], isLoading: opsLoading } = useOpsAuditLog(
    opsFilter !== "all" ? { tableName: opsFilter } : undefined
  );
  const { data: ledgerRows = [], isLoading: ledgerLoading } = useLedgerAuditLog();

  // Templates
  const { data: templates = [], isLoading: tplLoading } = useEstimateTemplates();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState<EstimateTemplate | null>(null);
  const [tplName, setTplName] = useState("");
  const [tplCategory, setTplCategory] = useState("");
  const [tplLaborMarkup, setTplLaborMarkup] = useState("35");
  const [tplMaterialMarkup, setTplMaterialMarkup] = useState("20");
  const [tplLines, setTplLines] = useState<TemplateLineItem[]>([]);

  // Handle OAuth redirect params
  useEffect(() => {
    const qbConnected = searchParams.get("qb_connected");
    const qbError = searchParams.get("qb_error");

    if (qbConnected === "true") {
      toast.success("QuickBooks connected successfully!");
      refetch();
      refetchSyncLog();
    } else if (qbError) {
      toast.error(`QuickBooks connection failed: ${qbError}`);
      refetchSyncLog();
    }

    if (qbConnected || qbError) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = () => {
    if (!companyId) {
      toast.error("Unable to determine company ID. Please try again.");
      return;
    }
    window.location.href = `${QB_FUNCTIONS_BASE}?action=auth&tenant_id=${encodeURIComponent(companyId)}`;
  };

  const handleDisconnect = async () => {
    if (!companyId) { toast.error("Unable to determine company ID"); return; }
    setDisconnecting(true);
    try {
      const res = await fetch(QB_FUNCTIONS_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
        body: JSON.stringify({ action: "disconnect", company_id: companyId }),
      });
      if (!res.ok) throw new Error(await res.text() || "Disconnect failed");
      toast.success("QuickBooks disconnected");
      refetch();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to disconnect QuickBooks");
    } finally {
      setDisconnecting(false);
    }
  };

  const openNewTemplate = () => {
    setEditingTpl(null); setTplName(""); setTplCategory("");
    setTplLaborMarkup("35"); setTplMaterialMarkup("20");
    setTplLines([{ description: "", unit: "SF", sub_lines: [{ type: "labor", description: "", unit: "SF", quantity_mode: "auto", quantity_formula: null, sort_order: 0 }] }]);
    setTplDialogOpen(true);
  };

  const openEditTemplate = (tpl: EstimateTemplate) => {
    setEditingTpl(tpl); setTplName(tpl.name); setTplCategory(tpl.category);
    setTplLaborMarkup(String(tpl.labor_markup_pct)); setTplMaterialMarkup(String(tpl.material_markup_pct));
    setTplLines(tpl.line_items as TemplateLineItem[]);
    setTplDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name is required"); return; }
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      if (editingTpl) {
        await updateTemplate.mutateAsync({ id: editingTpl.id, name: tplName, category: tplCategory, line_items: tplLines, labor_markup_pct: parseFloat(tplLaborMarkup) || 35, material_markup_pct: parseFloat(tplMaterialMarkup) || 20 });
        toast.success("Template updated");
      } else {
        await createTemplate.mutateAsync({ company_id: companyId, name: tplName, category: tplCategory, line_items: tplLines, labor_markup_pct: parseFloat(tplLaborMarkup) || 35, material_markup_pct: parseFloat(tplMaterialMarkup) || 20 });
        toast.success("Template created");
      }
      setTplDialogOpen(false);
    } catch (err: any) { toast.error(err.message || "Failed to save template"); }
  };

  const handleDuplicateTemplate = async (tpl: EstimateTemplate) => {
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      await createTemplate.mutateAsync({ company_id: companyId, name: `${tpl.name} (Copy)`, category: tpl.category, line_items: tpl.line_items as TemplateLineItem[], labor_markup_pct: tpl.labor_markup_pct, material_markup_pct: tpl.material_markup_pct });
      toast.success("Template duplicated");
    } catch (err: any) { toast.error(err.message || "Failed to duplicate"); }
  };

  const handleDeleteTemplate = async (id: string) => {
    try { await deleteTemplate.mutateAsync(id); toast.success("Template deleted"); }
    catch (err: any) { toast.error(err.message || "Failed to delete template"); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Settings</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          {isAdmin && <TabsTrigger value="company">Company</TabsTrigger>}
          {(isAdmin || can("invite_users")) && <TabsTrigger value="users">Users</TabsTrigger>}
          {(isAdmin || can("manage_roles")) && <TabsTrigger value="roles">Roles</TabsTrigger>}
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="everify">E-Verify</TabsTrigger>
          <TabsTrigger value="products">Products & Services</TabsTrigger>
          <TabsTrigger value="coa">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="estimate_defaults">Estimate Defaults</TabsTrigger>
          <TabsTrigger value="work_orders">Work Orders</TabsTrigger>
          {isAdmin && <TabsTrigger value="financial_controls">Financial Controls</TabsTrigger>}
          {isAdmin && <TabsTrigger value="numbering">Numbering</TabsTrigger>}
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        {isAdmin && (
          <TabsContent value="company" className="pt-2">
            <CompanyProfileTab />
          </TabsContent>
        )}

        <TabsContent value="users" className="pt-2">
          <UsersTab />
        </TabsContent>

        <TabsContent value="roles" className="pt-2">
          <RolesTab />
        </TabsContent>

        <TabsContent value="everify" className="space-y-4 pt-2">
          <EVerifySettingsSection />
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4 pt-2">
          <GoogleCalendarConnect />
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>QuickBooks Integration</CardTitle>
                  <CardDescription>Connect your QuickBooks account to sync financial data.</CardDescription>
                </div>
                {!isLoading && (
                  <Badge variant="outline" className={connected ? "bg-primary/15 text-primary border-primary/30" : "bg-destructive/15 text-destructive border-destructive/30"}>
                    {connected ? "Connected" : "Disconnected"}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Checking connection status…</div>
              ) : (
                <>
                  {connected && lastSyncedAt && <p className="text-sm text-muted-foreground">Last synced: {format(new Date(lastSyncedAt), "PPpp")}</p>}
                  {connected ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" onClick={() => setImportWizardOpen(true)} disabled={!companyId}>
                          <Download className="h-4 w-4 mr-1" /> Import Wizard
                        </Button>
                        <Button variant="destructive" onClick={handleDisconnect} disabled={disconnecting}>
                          {disconnecting && <Loader2 className="h-4 w-4 animate-spin" />} Disconnect QuickBooks
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={handleConnect}>Connect QuickBooks</Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* QB Sync Log */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Sync Log</CardTitle>
                  <CardDescription>Recent QuickBooks sync activity and errors.</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => refetchSyncLog()} disabled={syncLogLoading}>
                  <RefreshCw className={`h-4 w-4 ${syncLogLoading ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {syncLogLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
              ) : qbSyncLog.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sync activity yet.</p>
              ) : (
                <SyncLogGrouped logs={qbSyncLog as any[]} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="pt-2">
          <ProductsServicesTab />
        </TabsContent>

        <TabsContent value="coa" className="pt-2">
          <ChartOfAccountsTab />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4 pt-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Estimate Templates</h2>
            <Button size="sm" onClick={openNewTemplate}><Plus className="h-4 w-4 mr-1" /> New Template</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Lines</TableHead>
                    <TableHead className="text-right">Labor %</TableHead>
                    <TableHead className="text-right">Material %</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tplLoading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></TableCell></TableRow>
                  ) : templates.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No templates yet</TableCell></TableRow>
                  ) : templates.map((tpl) => (
                    <TableRow key={tpl.id}>
                      <TableCell className="font-medium">{tpl.name}</TableCell>
                      <TableCell>{tpl.category || "—"}</TableCell>
                      <TableCell className="text-center">{(tpl.line_items as TemplateLineItem[]).length}</TableCell>
                      <TableCell className="text-right">{tpl.labor_markup_pct}%</TableCell>
                      <TableCell className="text-right">{tpl.material_markup_pct}%</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditTemplate(tpl)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDuplicateTemplate(tpl)}><Copy className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteTemplate(tpl.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
            <DialogContent className="max-w-[900px] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTpl ? "Edit Template" : "New Template"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div><Label>Name *</Label><Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Flooring - LVP Full Install" /></div>
                <div><Label>Category</Label><Input value={tplCategory} onChange={(e) => setTplCategory(e.target.value)} placeholder="e.g. Flooring" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Labor Markup %</Label><Input type="number" value={tplLaborMarkup} onChange={(e) => setTplLaborMarkup(e.target.value)} /></div>
                  <div><Label>Material Markup %</Label><Input type="number" value={tplMaterialMarkup} onChange={(e) => setTplMaterialMarkup(e.target.value)} /></div>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm font-semibold">Template Lines</Label>
                  <p className="text-xs text-muted-foreground mb-3">Define the line items and sub-lines that will be created when this template is applied to an estimate.</p>
                  <TemplateLineEditor lines={tplLines} onChange={setTplLines} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTplDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveTemplate} disabled={createTemplate.isPending || updateTemplate.isPending}>
                  {(createTemplate.isPending || updateTemplate.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  {editingTpl ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="estimate_defaults" className="space-y-4 pt-2">
          <EstimateProposalPresetsSection />
        </TabsContent>

        <TabsContent value="work_orders" className="space-y-4 pt-2">
          <WODefaultsSection />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="financial_controls" className="pt-2 space-y-8">
            <TimesheetApprovalSection />
            <BillingLockBypassSection />
            <ApprovalRulesTab />
            <AlertThresholdsTab />
          </TabsContent>
        )}

        <TabsContent value="appearance" className="pt-2">
          <AppearanceTab />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="numbering" className="pt-2">
            <NumberingTab />
          </TabsContent>
        )}

        <TabsContent value="audit" className="space-y-4 pt-2">
          <RequirePermission anyOf={["view_financials", "approve_bills", "approve_payments"]}>
            <Tabs defaultValue="ops">
              <TabsList>
                <TabsTrigger value="ops">Operations</TabsTrigger>
                <TabsTrigger value="ledger">Ledger</TabsTrigger>
              </TabsList>
              <TabsContent value="ops" className="space-y-3">
                <Select value={opsFilter} onValueChange={setOpsFilter}>
                  <SelectTrigger className="w-48"><SelectValue placeholder="Filter by table" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tables</SelectItem>
                    <SelectItem value="purchase_orders">Purchase Orders</SelectItem>
                    <SelectItem value="job_orders">Job Orders</SelectItem>
                    <SelectItem value="change_orders">Change Orders</SelectItem>
                  </SelectContent>
                </Select>
                <AuditLogTable rows={opsRows} isLoading={opsLoading} variant="ops" />
              </TabsContent>
              <TabsContent value="ledger">
                <AuditLogTable rows={ledgerRows} isLoading={ledgerLoading} variant="ledger" />
              </TabsContent>
            </Tabs>
          </RequirePermission>
        </TabsContent>
      </Tabs>

      {companyId && (
        <QBImportWizard
          open={importWizardOpen}
          onOpenChange={(v) => { setImportWizardOpen(v); if (!v) refetchSyncLog(); }}
          companyId={companyId}
        />
      )}
    </div>
  );
};

export default Settings;

const WO_TOGGLE_SECTIONS = [
  { title: "Project Info", keys: [
    { key: "show_project_name", label: "Project name & number" },
    { key: "show_site_address", label: "Site address" },
    { key: "show_site_contact", label: "Site contact" },
    { key: "show_mobilization_date", label: "Mobilization date" },
    { key: "show_completion_date", label: "Completion date" },
    { key: "show_other_subs", label: "Other subs on site" },
  ]},
  { title: "Scope", keys: [
    { key: "show_line_items", label: "Line items + qty + rate" },
    { key: "show_product_specs", label: "Product specifications" },
    { key: "show_special_instructions", label: "Special instructions" },
    { key: "show_sov_reference", label: "SOV reference numbers" },
    { key: "show_internal_codes", label: "Internal codes" },
  ]},
  { title: "Commercial Terms", keys: [
    { key: "show_wo_total", label: "Total WO value" },
    { key: "show_payment_terms", label: "Payment terms" },
    { key: "show_retainage", label: "Retainage" },
    { key: "show_lien_waiver_req", label: "Lien waiver required" },
    { key: "show_billing_instructions", label: "Billing instructions" },
  ]},
  { title: "Site Requirements", keys: [
    { key: "show_work_hours", label: "Work hours" },
    { key: "show_parking", label: "Parking instructions" },
    { key: "show_badge_requirements", label: "Badge requirements" },
    { key: "show_safety_requirements", label: "Safety requirements" },
  ]},
  { title: "Signatures", keys: [
    { key: "show_signature_block", label: "Signature block" },
    { key: "show_company_signature", label: "Company signature line" },
    { key: "show_sub_signature", label: "Sub signature line" },
    { key: "show_contractor_scope", label: "Contractor scope of work" },
  ]},
];

const WODefaultsSection = () => {
  const { data: saved, isLoading } = useWODocumentSettings();
  const upsert = useUpsertWODocumentSettings();
  const [toggles, setToggles] = useState<Record<string, boolean>>(DEFAULT_TOGGLES);
  const [footerText, setFooterText] = useState("");
  const [scopeText, setScopeText] = useState("");

  useEffect(() => {
    if (saved) {
      const loaded: Record<string, boolean> = {};
      TOGGLE_KEYS.forEach((k) => { loaded[k] = (saved as any)[k] ?? DEFAULT_TOGGLES[k]; });
      setToggles(loaded);
      setFooterText(saved.custom_footer_text ?? "");
      setScopeText(saved.contractor_scope_text ?? "");
    }
  }, [saved]);

  const toggle = (key: string) => setToggles((p) => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({ ...toggles, custom_footer_text: footerText || null, contractor_scope_text: scopeText || null } as any);
      toast.success("Work order defaults saved");
    } catch (e: any) { toast.error(e.message); }
  };

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Work Order Document Defaults</CardTitle>
        <CardDescription>Configure which sections appear by default when generating work order documents.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {WO_TOGGLE_SECTIONS.map((section) => (
            <div key={section.title} className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{section.title}</p>
              {section.keys.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox checked={toggles[key]} onCheckedChange={() => toggle(key)} />
                  {label}
                </label>
              ))}
            </div>
          ))}
        </div>

        <Separator />

        <div className="space-y-2">
          <Label>Contractor Scope of Work Template</Label>
          <p className="text-xs text-muted-foreground">This boilerplate text will appear before signatures on every generated work order. You can edit it per-WO in the preview.</p>
          <Textarea
            value={scopeText}
            onChange={(e) => setScopeText(e.target.value)}
            placeholder="e.g. Contractor shall furnish all labor, materials, equipment, and supervision necessary to complete the scope of work described herein…"
            rows={6}
            className="max-w-2xl"
          />
        </div>

        <Separator />

        <div className="space-y-2 max-w-md">
          <Label>Custom Footer Text</Label>
          <Input value={footerText} onChange={(e) => setFooterText(e.target.value)} placeholder="e.g. Your Company LLC · State" />
        </div>

        <Button onClick={handleSave} disabled={upsert.isPending}>
          {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
          Save Defaults
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Billing Lock Bypass Setting ───────────────────────────────────────────
const BillingLockBypassSection = () => {
  const qc = useQueryClient();

  const { data: bypass, isLoading } = useQuery({
    queryKey: ["billing_bypass_contract_lock"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return true;
      const { data } = await (supabase.from("numbering_config") as any)
        .select("billing_bypass_contract_lock")
        .eq("company_id", companyId)
        .maybeSingle();
      return data?.billing_bypass_contract_lock ?? true;
    },
  });

  const update = useMutation({
    mutationFn: async (value: boolean) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const { error } = await (supabase.from("numbering_config") as any)
        .update({ billing_bypass_contract_lock: value })
        .eq("company_id", companyId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing_bypass_contract_lock"] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contract Lock — Billing Override</CardTitle>
        <CardDescription>
          When a contract is hard locked, database triggers normally block all modifications to
          linked records — including billing operations. Enable this to allow bill creation,
          approval, payment, and voiding to proceed even when the contract is locked.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between max-w-lg">
          <div>
            <p className="text-sm font-medium">Allow billing on hard-locked contracts</p>
            <p className="text-xs text-muted-foreground">
              {bypass
                ? "Billing operations bypass the contract lock. Creating, approving, and paying bills will always work."
                : "Billing operations are subject to the contract lock. Bills cannot be created or modified on locked contracts."}
            </p>
          </div>
          <Switch
            checked={bypass ?? true}
            disabled={isLoading || update.isPending}
            onCheckedChange={(checked) => {
              update.mutate(checked, {
                onSuccess: () => toast.success(checked ? "Billing lock bypass enabled" : "Billing lock bypass disabled"),
                onError: (err: any) => toast.error(err.message ?? "Failed to update setting"),
              });
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const TimesheetApprovalSection = () => {
  const { approvalRequired, isLoading, update } = useTimesheetApprovalSetting();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Timesheet Approval</CardTitle>
        <CardDescription>Control whether timesheets require approval before they can be invoiced.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between max-w-lg">
          <div>
            <p className="text-sm font-medium">Require approval before invoicing</p>
            <p className="text-xs text-muted-foreground">
              {approvalRequired
                ? "Timesheets must be submitted and approved before generating invoices."
                : "Timesheets skip approval and go directly to approved upon submission."}
            </p>
          </div>
          <Switch
            checked={approvalRequired}
            disabled={isLoading || update.isPending}
            onCheckedChange={(checked) => {
              update.mutate(checked, {
                onSuccess: () => toast.success(checked ? "Timesheet approval enabled" : "Timesheet approval disabled"),
                onError: (err: any) => toast.error(err.message ?? "Failed to update setting"),
              });
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const EVerifySettingsSection = () => {
  const [employerId, setEmployerId] = useState("");
  const [i9Required, setI9Required] = useState(true);
  const [everifyRequired, setEverifyRequired] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>E-Verify & I-9 Settings</CardTitle>
        <CardDescription>Configure employment verification requirements for contract labor.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-md space-y-2">
          <Label>E-Verify Employer ID</Label>
          <Input value={employerId} onChange={e => setEmployerId(e.target.value)} placeholder="From E-Verify enrollment" />
        </div>
        <Separator />
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Checkbox id="i9_req" checked={i9Required} onCheckedChange={v => setI9Required(!!v)} />
            <div>
              <Label htmlFor="i9_req">Require I-9 for all contract labor</Label>
              <p className="text-xs text-muted-foreground">Recommended — protects against employee misclassification</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox id="ev_req" checked={everifyRequired} onCheckedChange={v => setEverifyRequired(!!v)} />
            <div>
              <Label htmlFor="ev_req">Require E-Verify for contract labor</Label>
              <p className="text-xs text-muted-foreground">Check if required by your GC contracts or federal/state projects</p>
            </div>
          </div>
        </div>
        <Button onClick={() => toast.success("E-Verify settings saved")}>Save Settings</Button>
      </CardContent>
    </Card>
  );
};
